import React, { useState, useRef, useCallback, useMemo } from 'react';
import {
  useTable,
  appTableFeatures,
  ColumnDef,
  CellContext,
} from '@/lib/tanstack-table';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/components/ui/table';
import { Input } from 'src/components/ui/input';
import { Button as IconButton } from 'src/components/ui/button';
import MDBox from 'src/components/md/MDBox';
import MDTypography from 'src/components/md/MDTypography';
import MDButton from 'src/components/md/MDButton';
import {
  CheckCircleIcon,
  WarningIcon,
  InfoIcon,
  TrashIcon,
  ArrowUUpLeftIcon,
  ProhibitIcon,
  PlusIcon,
} from '@phosphor-icons/react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { LineItem } from 'src/types/application/SalesOrderDraftDTO';
import CustomerProductMatch, { hasMatch } from './CustomerProductMatch';
import { SalesOrderDraftAssociateLineItemPopover } from '../SalesOrderDraftAssociateLineItemPopover';
import { useFeatureFlag } from '@/integrations/feature-flags/feature-flags';
import { useGT } from 'gt-react';

/** Appends noon UTC to YYYY-MM-DD date-only strings for correct local display */

// Types for cell navigation
type EditableField =
  | 'quantity'
  | 'unitPrice'
  | 'rescheduledDate'
  | 'customerDescription'
  | 'customerIdentifier';
interface Props {
  lineItems: LineItem[];
  editable: boolean;
  isLoading: boolean;
  onDeleteLineItem: (index: number) => void;
  onAddLineItem: () => void;
  onUpdateLineItem?: (
    index: number,
    field:
      | 'quantity'
      | 'unitPrice'
      | 'rescheduledDate'
      | 'customerDescription'
      | 'customerIdentifier',
    value: number | string,
  ) => void;
  pendingUpdates?: Record<
    number,
    Set<
      | 'quantity'
      | 'unitPrice'
      | 'rescheduledDate'
      | 'customerDescription'
      | 'customerIdentifier'
    >
  >;
  onSelectLineItem?: (index: number) => void;
  isEdiSource?: boolean;
  onToggleRejectLineItem?: (index: number) => void;
  customerId?: string;
  customerName?: string;
  onAssociateVariantSelected?: (
    rowIndex: number,
    item: LineItem,
    productVariantId?: string,
  ) => void;
}

// Feature flag for showing total column
const SHOW_TOTAL_COLUMN = false;

type CellPosition = {
  rowIndex: number;
  field: EditableField;
};

// Navigation manager hook
const useTableNavigation = (
  lineItems: LineItem[],
  _onUpdateLineItem?: (
    index: number,
    field: EditableField,
    value: number | string,
  ) => void,
) => {
  const [currentEditingCell, setCurrentEditingCell] =
    useState<CellPosition | null>(null);
  const [isNavigationActive, setIsNavigationActive] = useState(false);
  const cellRefs = useRef<
    Map<string, { startEdit: () => void; saveAndExit: () => void }>
  >(new Map());
  const tableContainerRef = useRef<HTMLDivElement>(null);

  // Get all editable fields based on email source
  const getEditableFields = useCallback((): EditableField[] => {
    const fields: EditableField[] = [
      'customerIdentifier',
      'customerDescription',
      'quantity',
      'unitPrice',
    ];
    return fields;
  }, []);

  // Get cell key for referencing
  const getCellKey = useCallback((rowIndex: number, field: EditableField) => {
    return `${rowIndex}-${field}`;
  }, []);

  // Register cell ref
  const registerCellRef = useCallback(
    (
      rowIndex: number,
      field: EditableField,
      ref: { startEdit: () => void; saveAndExit: () => void },
    ) => {
      const key = getCellKey(rowIndex, field);
      cellRefs.current.set(key, ref);
    },
    [getCellKey],
  );

  // Navigate to next/previous cell
  const navigateToCell = useCallback(
    (direction: 'up' | 'down' | 'left' | 'right' | 'tab' | 'shift-tab') => {
      if (!currentEditingCell) return false;

      const { rowIndex, field } = currentEditingCell;
      const editableFields = getEditableFields();
      const currentFieldIndex = editableFields.indexOf(field);

      let newRowIndex = rowIndex;
      let newFieldIndex = currentFieldIndex;

      switch (direction) {
        case 'left':
        case 'shift-tab':
          newFieldIndex = currentFieldIndex - 1;
          if (newFieldIndex < 0) {
            newFieldIndex = editableFields.length - 1;
            newRowIndex = rowIndex - 1;
          }
          break;
        case 'right':
        case 'tab':
          newFieldIndex = currentFieldIndex + 1;
          if (newFieldIndex >= editableFields.length) {
            newFieldIndex = 0;
            newRowIndex = rowIndex + 1;
          }
          break;
        case 'up':
          newRowIndex = rowIndex - 1;
          break;
        case 'down':
          newRowIndex = rowIndex + 1;
          break;
      }

      // Check bounds
      if (newRowIndex < 0 || newRowIndex >= lineItems.length) {
        return false;
      }

      // Save current cell before navigating away
      const currentCellKey = getCellKey(rowIndex, field);
      const currentCellRef = cellRefs.current.get(currentCellKey);
      if (currentCellRef) {
        currentCellRef.saveAndExit();
      }

      // Navigate to the new cell
      const newField = editableFields[newFieldIndex];
      const newCellKey = getCellKey(newRowIndex, newField);
      const newCellRef = cellRefs.current.get(newCellKey);

      if (newCellRef) {
        setCurrentEditingCell({ rowIndex: newRowIndex, field: newField });
        // Small delay to ensure previous cell has exited edit mode
        setTimeout(() => {
          newCellRef.startEdit();
        }, 10);
        return true;
      }

      return false;
    },
    [currentEditingCell, getEditableFields, getCellKey, lineItems.length],
  );

  // Start editing a cell
  const startEditingCell = useCallback(
    (rowIndex: number, field: EditableField) => {
      setCurrentEditingCell({ rowIndex, field });
      setIsNavigationActive(true);
    },
    [],
  );

  // Stop editing but keep navigation active
  const stopEditingCell = useCallback(() => {
    setCurrentEditingCell(null);
    // Don't deactivate navigation - only do that on explicit exit
  }, []);

  // Exit navigation mode completely
  const exitNavigationMode = useCallback(() => {
    // Save current cell if it exists
    if (currentEditingCell) {
      const currentCellKey = getCellKey(
        currentEditingCell.rowIndex,
        currentEditingCell.field,
      );
      const currentCellRef = cellRefs.current.get(currentCellKey);
      if (currentCellRef) {
        currentCellRef.saveAndExit();
      }
    }
    setCurrentEditingCell(null);
    setIsNavigationActive(false);
  }, [currentEditingCell, getCellKey]);

  // Global keyboard event handler for navigation
  React.useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if (!isNavigationActive) return;

      // Handle Escape to exit navigation mode
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        exitNavigationMode();
        return;
      }

      // Handle navigation keys
      if (
        ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(
          e.key,
        )
      ) {
        e.preventDefault();
        e.stopPropagation();

        if (currentEditingCell) {
          const direction =
            e.key === 'ArrowLeft'
              ? 'left'
              : e.key === 'ArrowRight'
                ? 'right'
                : e.key === 'ArrowUp'
                  ? 'up'
                  : e.key === 'ArrowDown'
                    ? 'down'
                    : e.shiftKey
                      ? 'shift-tab'
                      : 'tab';
          navigateToCell(direction);
        }
      }
    };

    if (isNavigationActive) {
      document.addEventListener('keydown', handleGlobalKeyDown, true);
    }

    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown, true);
    };
  }, [
    isNavigationActive,
    currentEditingCell,
    navigateToCell,
    exitNavigationMode,
  ]);

  // Handle clicks outside the table
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isNavigationActive &&
        tableContainerRef.current &&
        !tableContainerRef.current.contains(event.target as Node)
      ) {
        // Only exit navigation if clicking outside the table
        exitNavigationMode();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isNavigationActive, exitNavigationMode]);

  return {
    currentEditingCell,
    isNavigationActive,
    navigateToCell,
    startEditingCell,
    stopEditingCell,
    exitNavigationMode,
    registerCellRef,
    tableContainerRef,
  };
};

// Inline editable cell component
const EditableCell = ({
  value,
  onSave,
  inputType = 'number',
  type = 'number',
  prefix = '',
  isPending = false,
  rowIndex,
  field,
  navigationManager,
  placeholder = '',
}: {
  value: number | string | undefined;
  onSave: (value: number | string) => void;
  inputType?: 'number' | 'text';
  type?: 'number' | 'integer';
  prefix?: string;
  isPending?: boolean;
  rowIndex: number;
  field: EditableField;
  navigationManager: ReturnType<typeof useTableNavigation>;
  placeholder?: string;
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value?.toString() || '');
  const inputRef = useRef<HTMLInputElement>(null);

  const {
    currentEditingCell,
    startEditingCell,
    stopEditingCell,
    registerCellRef,
  } = navigationManager;

  // Check if this cell is currently being edited
  const isCurrentlyEditing =
    currentEditingCell?.rowIndex === rowIndex &&
    currentEditingCell?.field === field;

  // Sync editing state with navigation manager
  React.useEffect(() => {
    if (isCurrentlyEditing && !isEditing) {
      setIsEditing(true);
      setEditValue(value?.toString() || '');
    } else if (!isCurrentlyEditing && isEditing) {
      setIsEditing(false);
    }
  }, [isCurrentlyEditing, isEditing, value]);

  // Auto-focus when entering edit mode
  React.useEffect(() => {
    if (isEditing && inputRef.current) {
      // Small delay to ensure the input is fully rendered
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          // Only call select if it's available (not all input types support it)
          if (typeof inputRef.current.select === 'function') {
            inputRef.current.select();
          }
        }
      }, 0);
    }
  }, [isEditing]);

  const handleSave = useCallback(() => {
    if (inputType === 'text') {
      const trimmedValue = editValue.trim();
      // Only call onSave if the value actually changed
      if (trimmedValue !== value) {
        onSave(trimmedValue);
      }
    } else {
      const numValue = parseFloat(editValue);
      if (!isNaN(numValue) && numValue >= 0) {
        // Only call onSave if the value actually changed
        if (numValue !== value) {
          onSave(numValue);
        }
      }
    }
    setIsEditing(false);
    stopEditingCell();
  }, [editValue, value, onSave, stopEditingCell, inputType]);

  // Register this cell with the navigation manager
  React.useEffect(() => {
    registerCellRef(rowIndex, field, {
      startEdit: () => {
        setIsEditing(true);
        setEditValue(value?.toString() || '');
      },
      saveAndExit: () => {
        handleSave();
      },
    });
  }, [registerCellRef, rowIndex, field, value, handleSave]);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    // Only handle Enter and Escape keys locally
    // Navigation keys are handled globally by the navigation manager
    if (e.key === 'Enter') {
      e.stopPropagation();
      handleSave();
    } else if (e.key === 'Escape') {
      e.stopPropagation();
      // On escape, revert to original value and exit editing
      setEditValue(value?.toString() || '');
      setIsEditing(false);
      stopEditingCell();
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    // Select all text when the field gets focus
    if (typeof e.target.select === 'function') {
      e.target.select();
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isEditing) {
      startEditingCell(rowIndex, field);
      setIsEditing(true);
    }
  };

  if (isEditing) {
    return (
      <MDBox
        display="flex"
        alignItems="center"
        gap={0.5}
        onClick={(e: React.MouseEvent<HTMLElement>) => e.stopPropagation()}
      >
        <Input
          ref={inputRef}
          className="w-full h-7 text-xs px-2 py-1 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          value={editValue}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setEditValue(e.target.value)
          }
          onKeyDown={handleKeyPress}
          onBlur={handleSave}
          onFocus={handleFocus}
          autoFocus
          type={inputType === 'text' ? 'text' : 'number'}
          placeholder={placeholder}
          {...(inputType !== 'text' && {
            min: 0,
            step: type === 'integer' ? 1 : 0.01,
          })}
        />
      </MDBox>
    );
  }

  // Format display value based on type
  const formatValue = (val: number | string | undefined) => {
    if (inputType === 'text') {
      return val?.toString() || placeholder;
    }

    if (type === 'integer') {
      // For quantity: show whole numbers unless there's a decimal
      const numVal = Number(val);
      return numVal % 1 === 0 ? numVal.toString() : numVal.toFixed(2);
    } else {
      // For currency: always show 2 decimal places
      return Number(val).toFixed(2);
    }
  };

  return (
    <MDBox
      display="flex"
      alignItems="center"
      sx={{
        cursor: 'pointer',
        padding: '4px 8px',
        borderRadius: '4px',
        minHeight: '24px',
        transition: 'all 0.2s ease',
        '&:hover': {
          backgroundColor: 'rgba(25, 118, 210, 0.08)',
          border: '1px solid rgba(25, 118, 210, 0.3)',
        },
        border: isPending
          ? '1px solid rgba(25, 118, 210, 0.5)'
          : '1px solid transparent',
        backgroundColor: isPending ? 'rgba(25, 118, 210, 0.05)' : 'transparent',
        ...(isPending && {
          animation: 'pulse 2s infinite',
          '@keyframes pulse': {
            '0%': {
              backgroundColor: 'rgba(25, 118, 210, 0.05)',
              borderColor: 'rgba(25, 118, 210, 0.5)',
            },
            '50%': {
              backgroundColor: 'rgba(25, 118, 210, 0.1)',
              borderColor: 'rgba(25, 118, 210, 0.7)',
            },
            '100%': {
              backgroundColor: 'rgba(25, 118, 210, 0.05)',
              borderColor: 'rgba(25, 118, 210, 0.5)',
            },
          },
        }),
      }}
      onClick={handleClick}
    >
      <MDTypography
        variant="caption"
        color={inputType === 'text' && !value ? 'text.secondary' : undefined}
        sx={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          maxWidth: '100%',
          display: 'block',
        }}
        title={
          inputType === 'text'
            ? formatValue(value)
            : value
              ? `${prefix}${formatValue(value)}`
              : '-'
        }
      >
        {inputType === 'text'
          ? formatValue(value)
          : value
            ? `${prefix}${formatValue(value)}`
            : '-'}
      </MDTypography>
    </MDBox>
  );
};

const LineItemsTable: React.FC<Props> = ({
  lineItems,
  editable,
  isLoading,
  onDeleteLineItem,
  onAddLineItem,
  onUpdateLineItem,
  pendingUpdates = {},
  onSelectLineItem,
  isEdiSource,
  onToggleRejectLineItem,
  customerId,
  onAssociateVariantSelected,
}) => {
  const gt = useGT();
  const { enabled: isAssociateProductV2Enabled } = useFeatureFlag(
    'associate-product-v2-enabled',
    false,
  );

  // Initialize navigation manager
  const navigationManager = useTableNavigation(lineItems, onUpdateLineItem);

  // Column definitions following CustomerTable pattern
  const columns: ColumnDef<LineItem>[] = useMemo(
    () => [
      {
        accessorKey: 'status',
        header: '',
        cell: (info) => {
          const item = info.row.original;
          const hasCustomerProduct = !!item.selectedCustomerProductId;
          const hasProductVariant = !!item.selectedProductVariantId;
          // EDI lines carry only the translation-list entry — the Famous
          // catalog syncs separately, so a correctly mapped line often has no
          // variant. Omitting it here flags orderable lines as unmatched.
          // Same three-way predicate as `unmatchedLineItemCount` in the modal.
          const hasTranslationListItem = !!item.selectedTranslationListItemId;
          const isMatched =
            hasCustomerProduct || hasProductVariant || hasTranslationListItem;

          const tooltipText = isMatched
            ? 'Item is matched. Click to change.'
            : 'Click to match this item to a product.';

          // EDI purchase orders (850/875) resolve their products solely from
          // the customer's Famous translation list. Re-mapping a line by hand
          // would write a manual product reference over that list-derived one,
          // so the control is display-only here — fix the mapping in Famous
          // and refresh the translation list instead.
          if (isEdiSource) {
            return (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center justify-center w-full h-full p-1">
                    {isMatched ? (
                      <CheckCircleIcon className="h-4 w-4 text-green-600 flex-shrink-0" />
                    ) : (
                      <WarningIcon className="h-4 w-4 text-amber-500 flex-shrink-0" />
                    )}
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <span className="text-xs">
                    {gt(
                      'Product mapping comes from the EDI translation list and cannot be changed here.',
                      {
                        context:
                          'tooltip explaining product mapping is read-only on EDI orders',
                      },
                    )}
                  </span>
                </TooltipContent>
              </Tooltip>
            );
          }

          if (isAssociateProductV2Enabled && customerId) {
            // The tooltip is composed inside the popover (both triggers must
            // `asChild` onto the same leaf element). Wrapping the popover in an
            // outer Tooltip here instead collapses the nested triggers and the
            // popover never opens on click.
            return (
              <SalesOrderDraftAssociateLineItemPopover
                lineItem={item}
                customerId={customerId}
                tooltipText={tooltipText}
                onVariantSelected={({ productVariantId }) => {
                  onAssociateVariantSelected?.(
                    info.row.index,
                    item,
                    productVariantId,
                  );
                }}
              />
            );
          }

          return (
            <Tooltip>
              <TooltipTrigger asChild>
                <div
                  className="flex items-center justify-center w-full h-full cursor-pointer rounded p-1 hover:bg-muted"
                  onClick={() => {
                    if (onSelectLineItem) {
                      onSelectLineItem(info.row.index);
                    }
                  }}
                >
                  {isMatched ? (
                    <CheckCircleIcon className="h-4 w-4 text-green-600 flex-shrink-0" />
                  ) : (
                    <WarningIcon className="h-4 w-4 text-amber-500 flex-shrink-0" />
                  )}
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <span className="text-xs">{tooltipText}</span>
              </TooltipContent>
            </Tooltip>
          );
        },
        size: 40,
      },
      {
        accessorKey: 'customerIdentifier',
        header: () => (
          <MDBox display="flex" alignItems="center" gap={0.8}>
            ID
            <Tooltip>
              <TooltipTrigger asChild>
                <MDBox sx={{ display: 'inline-flex', alignItems: 'center' }}>
                  <InfoIcon style={{ opacity: 0.7 }} size={16} />
                </MDBox>
              </TooltipTrigger>
              <TooltipContent>
                Customer&apos;s product identifier (e.g. SKU, UPC, GTIN, etc.)
              </TooltipContent>
            </Tooltip>
          </MDBox>
        ),
        cell: (info) => {
          const item = info.row.original;
          const identifier = item.customerIdentifier || item.sku || item.upc;

          return (
            <MDBox display="flex" flexDirection="column">
              {editable && onUpdateLineItem ? (
                <EditableCell
                  value={item.customerIdentifier || ''}
                  onSave={(newValue) =>
                    onUpdateLineItem(
                      info.row.index,
                      'customerIdentifier',
                      newValue,
                    )
                  }
                  inputType="text"
                  isPending={pendingUpdates[info.row.index]?.has(
                    'customerIdentifier',
                  )}
                  rowIndex={info.row.index}
                  field="customerIdentifier"
                  navigationManager={navigationManager}
                />
              ) : identifier ? (
                <MDTypography
                  variant="caption"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '100%',
                    display: 'block',
                  }}
                  title={identifier}
                >
                  {identifier}
                </MDTypography>
              ) : (
                <MDTypography variant="caption" color="text.secondary">
                  -
                </MDTypography>
              )}
            </MDBox>
          );
        },
        size: 60,
      },
      {
        accessorKey: 'customerDescription',
        header: () => (
          <MDBox display="flex" alignItems="center" gap={0.8}>
            {gt('Item', { context: 'sales order line items table column' })}
            <Tooltip>
              <TooltipTrigger>
                <InfoIcon className="opacity-70" size={16} />
              </TooltipTrigger>
              <TooltipContent>
                {gt("Customer's product description", {
                  context: 'tooltip for item column on sales order lines',
                })}
              </TooltipContent>
            </Tooltip>
          </MDBox>
        ),
        cell: (info) => {
          const item = info.row.original;

          const description =
            item.customerDescription ||
            item.itemName ||
            item.name ||
            gt('No description available', {
              context: 'placeholder when line item has no description',
            });

          return (
            <MDBox display="flex" flexDirection="column">
              {editable && onUpdateLineItem ? (
                <EditableCell
                  value={item.customerDescription || ''}
                  onSave={(newValue) =>
                    onUpdateLineItem(
                      info.row.index,
                      'customerDescription',
                      newValue,
                    )
                  }
                  inputType="text"
                  isPending={pendingUpdates[info.row.index]?.has(
                    'customerDescription',
                  )}
                  rowIndex={info.row.index}
                  field="customerDescription"
                  navigationManager={navigationManager}
                />
              ) : (
                <MDTypography variant="caption" color="text.secondary">
                  {description ||
                    gt('No description available', {
                      context: 'placeholder when line item has no description',
                    })}
                </MDTypography>
              )}
            </MDBox>
          );
        },
      },
      {
        accessorKey: 'quantity',
        header: 'Quantity',
        cell: (info) => {
          const value = info.getValue() as number;

          if (editable && onUpdateLineItem) {
            return (
              <EditableCell
                value={value}
                onSave={(newValue) =>
                  onUpdateLineItem(info.row.index, 'quantity', newValue)
                }
                type="integer"
                isPending={pendingUpdates[info.row.index]?.has('quantity')}
                rowIndex={info.row.index}
                field="quantity"
                navigationManager={navigationManager}
              />
            );
          }

          // Format quantity display: show whole numbers unless there's a decimal
          const displayValue = value
            ? value % 1 === 0
              ? value.toString()
              : value.toFixed(2)
            : '-';

          return <MDTypography variant="caption">{displayValue}</MDTypography>;
        },
        size: 60,
      },
      {
        accessorKey: 'unitPrice',
        header: 'Unit Price',
        cell: (info) => {
          const value = info.getValue() as number;

          if (editable && onUpdateLineItem) {
            return (
              <EditableCell
                value={value}
                onSave={(newValue) =>
                  onUpdateLineItem(info.row.index, 'unitPrice', newValue)
                }
                prefix="$"
                isPending={pendingUpdates[info.row.index]?.has('unitPrice')}
                rowIndex={info.row.index}
                field="unitPrice"
                navigationManager={navigationManager}
              />
            );
          }

          return (
            <MDTypography variant="caption">
              {value ? `$${value.toFixed(2)}` : '-'}
            </MDTypography>
          );
        },
        size: 80,
      },
      // The Rescheduled Date column used to live here, EDI-only. Removed:
      // across 16 months and 384 EDI orders on Pennrose not one line ever
      // carried a value (the only 5 in the table are email-sourced, from a
      // single day in July 2025, entered on the sales-order page). The
      // `rescheduled_date` column, the staged -> sales-order plumbing and the
      // 855 `DR` acknowledgment logic that reads it are all intact, so putting
      // the column back is a UI-only change.
      // Conditionally include total column
      ...(SHOW_TOTAL_COLUMN
        ? [
            {
              accessorKey: 'total',
              header: 'Total',
              cell: (info) => {
                const item = info.row.original;
                const total =
                  item.quantity && item.unitPrice
                    ? item.quantity * item.unitPrice
                    : item.amount;

                return (
                  <MDTypography variant="caption">
                    {total ? `$${total.toFixed(2)}` : '-'}
                  </MDTypography>
                );
              },
              size: 120,
            } as ColumnDef<LineItem>,
          ]
        : []),
      ...(editable
        ? [
            {
              accessorKey: 'actions',
              header: '',
              cell: (info: CellContext<LineItem, unknown>) => {
                const isRejected = info.row.original.ediRejected;

                return (
                  <MDBox display="flex" gap={0.5}>
                    {isEdiSource ? (
                      // For EDI sources: show reject/restore toggle
                      <IconButton
                        variant="ghost"
                        size="icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          onToggleRejectLineItem?.(info.row.index);
                        }}
                        disabled={isLoading}
                        className={`reject-action p-0.5 ${isRejected ? 'text-green-600' : 'text-amber-500'}`}
                      >
                        {isRejected ? (
                          <ArrowUUpLeftIcon size={14} />
                        ) : (
                          <ProhibitIcon size={14} />
                        )}
                      </IconButton>
                    ) : (
                      // For non-EDI sources: show delete
                      <IconButton
                        variant="ghost"
                        size="icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteLineItem(info.row.index);
                        }}
                        disabled={isLoading}
                        className="delete-action p-0.5 text-destructive hover:text-destructive"
                      >
                        <TrashIcon size={14} />
                      </IconButton>
                    )}
                  </MDBox>
                );
              },
              size: 25,
            } as ColumnDef<LineItem>,
          ]
        : []),
    ],
    [
      isAssociateProductV2Enabled,
      customerId,
      onAssociateVariantSelected,
      editable,
      onUpdateLineItem,
      isEdiSource,
      isLoading,
      onDeleteLineItem,
      onToggleRejectLineItem,
      pendingUpdates,
      navigationManager,
      onSelectLineItem,
      gt,
    ],
  );

  // Table setup following CustomerTable pattern
  const table = useTable({
    features: appTableFeatures,
    data: lineItems,
    columns,
  });

  return (
    <MDBox
      ref={navigationManager.tableContainerRef}
      p={2}
      border="1px solid"
      borderColor="grey.300"
      borderRadius="borderRadius"
      // Fills the slot the modal gives it; the rows scroll inside. The column
      // around it deliberately does not scroll, so this is the only scrollbar.
      sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
    >
      {editable && Math.random() > 2 ? (
        <MDBox
          mb={2}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{ flexShrink: 0 }}
        >
          <MDTypography variant="h6" fontWeight="medium">
            {gt('Line Items', { context: 'section heading sales order lines' })}
          </MDTypography>
          <MDButton
            variant="outlined"
            color="info"
            size="small"
            onClick={onAddLineItem}
            disabled={isLoading}
          >
            <PlusIcon size={16} style={{ marginRight: 4 }} />
            {gt('Add Line Item', { context: 'button to add sales order line' })}
          </MDButton>
        </MDBox>
      ) : (
        <MDBox
          mb={2}
          display="flex"
          justifyContent="flex-start"
          alignItems="center"
          sx={{ flexShrink: 0 }}
        >
          <MDTypography variant="h6" fontWeight="medium">
            {gt('Line Items', { context: 'section heading sales order lines' })}
          </MDTypography>
        </MDBox>
      )}

      <div className="flex-1 overflow-auto">
        <Table style={{ width: '100%', tableLayout: 'fixed' }}>
          <TableHeader>
            <TableRow>
              {table.getHeaderGroups().map((headerGroup) =>
                headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className="relative text-left whitespace-nowrap bg-transparent"
                    style={{
                      width: header.column.columnDef.size
                        ? `${header.column.columnDef.size}px`
                        : 'auto',
                      minWidth: header.column.columnDef.size
                        ? `${header.column.columnDef.size}px`
                        : 'auto',
                    }}
                  >
                    {header.isPlaceholder
                      ? null
                      : header.column.columnDef.header instanceof Function
                        ? header.column.columnDef.header(header.getContext())
                        : header.column.columnDef.header}
                  </TableHead>
                )),
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((row) => {
              const isRejected = row.original.ediRejected;

              return (
                <React.Fragment key={row.id}>
                  {/* Main line item row */}
                  <TableRow
                    className={
                      isRejected
                        ? 'opacity-60 line-through bg-black/[0.02] [&_.delete-action]:opacity-0 [&_.reject-action]:opacity-0 hover:[&_.delete-action]:opacity-100 hover:[&_.reject-action]:opacity-100'
                        : '[&_.delete-action]:opacity-0 [&_.reject-action]:opacity-0 hover:[&_.delete-action]:opacity-100 hover:[&_.reject-action]:opacity-100'
                    }
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        className={`text-[#7b809a] text-sm font-normal${isRejected ? ' line-through opacity-70' : ''}`}
                      >
                        {cell.column.columnDef.cell instanceof Function
                          ? cell.column.columnDef.cell(cell.getContext())
                          : String(cell.getValue() ?? '')}
                      </TableCell>
                    ))}
                  </TableRow>
                  {hasMatch(row.original) && (
                    <TableRow>
                      <TableCell colSpan={row.getVisibleCells().length}>
                        <CustomerProductMatch item={row.original} />
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </MDBox>
  );
};

export default LineItemsTable;
